import { Box } from '@chakra-ui/react';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useRegistrarFilter } from 'pages/resources/aors/hooks/useRegistrarFilter';
import { useRegistrarsMini } from 'pages/resources/aors/queries/useRegistrarsMini';
import useCreateAOR from './queries/useCreateAOR';

import FormBase from '@/components/forms/new/FormBase';
import { LoadingIndicator } from 'components/v4';
import { NoRegistrar } from 'pages/resources/aors/NoRegistrar';
import PasswordField from '../../resources/pbx/formFields/PasswordField';
import UsernameField from '../../resources/pbx/formFields/UsernameField';
import AdvancedTransferField from './formFields/AdvancedTransferField';
import AuthenticationField from './formFields/AuthenticationField';
import ExtensionField from './formFields/ExtensionField';
import RegistrarField from './formFields/RegistrarField';

import {
    aorsPath,
    ERROR_MESSAGES,
    addHeader as header,
    initialState,
    initValidationError,
    registrarSystems,
} from './constants';

const AORsAdd = () => {
    const navigate = useNavigate();
    const { darkMode } = useSelector((state) => state.settings);
    const { currentCompany } = useSelector((state) => state.navigationLists);
    const { restError } = useSelector((state) => state.modal);
    const [isDisabled, setIsDisabled] = useState(true);

    /**@example '/aors/{registrationId}?registrar={registrarId}' */
    const aorsPathWithRegistrar = `${aorsPath}${location.search}`;
    const breadCrumbTrail = [
        { label: 'Resources', path: `${location.search}` },
        {
            label: 'TRS Registration Management',
            path: aorsPathWithRegistrar,
        },
        { label: 'Add Registration', path: `${location.search}` },
    ];

    const [validationError, setValidationError] = useState(initValidationError);

    const {
        data: registrars = [],
        isLoading,
        isFetching,
    } = useRegistrarsMini(currentCompany);

    const { selectedRegistrarId } = useRegistrarFilter(registrars);
    const noRegistrars = registrars?.length === 0;

    const handleNavBack = () => {
        const backLink = [aorsPathWithRegistrar, { state: { index: 0 } }];
        if (Array.isArray(backLink)) {
            navigate(backLink[0], backLink[1]);
        } else {
            navigate(backLink);
        }
    };
    const handleSuccess = () => {
        toast.success('TRS Registration created successfully!');
        navigate(aorsPathWithRegistrar);
    };

    const handleError = (error) => {
        console.error('Failed to create TRS Registration:', error);
        toast.error(
            restError ?? 'Failed to create TRS Registration. Please try again.',
        );
    };

    const { mutate: createAOR, isPending } = useCreateAOR(
        handleSuccess,
        handleError,
    );

    const finalFormSubmit = async (values) => {
        // Reset validation error
        setValidationError(initValidationError);

        // Validate required fields
        if (!values.registrarID) {
            console.error(ERROR_MESSAGES.REGISTRAR_REQUIRED);
            setValidationError((prev) => ({
                ...prev,
                registrarID: ERROR_MESSAGES.REGISTRAR_REQUIRED,
            }));
            return;
        }

        if (!values.extension || values.extension.length < 3) {
            console.error(ERROR_MESSAGES.EXTENSION_REQUIRED);
            setValidationError((prev) => ({
                ...prev,
                extension: ERROR_MESSAGES.EXTENSION_REQUIRED,
            }));
            return;
        }

        // Validate credentials if override is disabled
        if (
            values.overrideCredentials &&
            (!values.username || !values.password)
        ) {
            const errorMessage =
                'Username and password are required when not using registrar authentication';
            console.error(errorMessage);
            setValidationError((prev) => ({
                ...prev,
                username: errorMessage,
                password: errorMessage,
            }));
            return;
        }

        const finalSubmit = {
            extension: values.extension,
            registrarID: values.registrarID,
            overrideCredentials: values.overrideCredentials,
            username: values.overrideCredentials ? values.username : '',
            password: values.overrideCredentials ? values.password : '',
            isEnabled: true,
            useAdvancedTransfer: values.useAdvancedTransfer || false,
        };

        createAOR(finalSubmit);
    };

    const formFields = (errors, touched, values) => {
        const hasValidationError = Object.values(validationError).some(
            (e) => e !== '',
        );
        const hasRegistrar = selectedRegistrarId || values?.registrarID;
        const hasExtension = values?.extension && values.extension.length >= 3;

        // Check if credentials are required when not using override
        const hasCredentials =
            !values?.overrideCredentials ||
            (values?.username && values?.password);
        const hasRequiredFields =
            hasRegistrar && hasExtension && hasCredentials;

        // Update form submission state
        if (hasRequiredFields && !hasValidationError) {
            setIsDisabled(false);
        } else {
            setIsDisabled(true);
        }

        const fields = [];

        // Always show Registrar field
        fields.push(
            <RegistrarField
                key="registrarID"
                errors={errors}
                touched={touched}
                values={values}
                darkMode={darkMode}
                registrars={registrars || registrarSystems}
                isDisabled={isPending}
                validationError={validationError}
            />,
        );

        // Progressive disclosure: Show Extension field after registrar is selected
        if (hasRegistrar) {
            fields.push(
                <ExtensionField
                    key="extension"
                    errors={errors}
                    touched={touched}
                    values={values}
                    darkMode={darkMode}
                    isDisabled={isPending}
                    validationError={validationError}
                />,
            );

            // Show Advanced Transfer field (wholesale feature)
            fields.push(
                <AdvancedTransferField
                    key="useAdvancedTransfer"
                    errors={errors}
                    touched={touched}
                    values={values}
                    darkMode={darkMode}
                    isDisabled={isPending}
                />,
            );

            // Show Authentication field after registrar is selected
            fields.push(
                <Box
                    key="overrideCredentials"
                    mt="0!important"
                    pb={values?.overrideCredentials ? 0 : '12px'}>
                    <AuthenticationField
                        errors={errors}
                        touched={touched}
                        values={values}
                        darkMode={darkMode}
                        isDisabled={isPending}
                        validationError={validationError}
                    />
                </Box>,
            );

            // Show Username/Password fields when authentication override is enabled
            if (values?.overrideCredentials) {
                fields.push(
                    <UsernameField
                        key="username"
                        errors={errors}
                        touched={touched}
                        darkMode={darkMode}
                        isDisabled={isPending}
                    />,
                );

                fields.push(
                    <Box key="password" mt="0!important" pb="12px">
                        <PasswordField
                            errors={errors}
                            touched={touched}
                            darkMode={darkMode}
                            isDisabled={isPending}
                        />
                    </Box>,
                );
            }
        }

        return fields;
    };

    const isFetchingData = isLoading || isFetching || isPending;

    if (isFetchingData) {
        return <LoadingIndicator />;
    }

    if (noRegistrars) {
        return <NoRegistrar onClick={handleNavBack} />;
    }

    return (
        <FormBase
            isDisabled={isDisabled}
            breadCrumbTrail={breadCrumbTrail}
            header={header}
            initialValues={initialState}
            finalFormSubmit={finalFormSubmit}
            backLink={[aorsPathWithRegistrar, { state: { index: 0 } }]}>
            {({ errors, touched, values }) =>
                formFields(errors, touched, values)
            }
        </FormBase>
    );
};

export default AORsAdd;
